import { Box, Button, Chip, Grid, Typography } from "@mui/material";
import { settingsReportStyles } from "../../../styles/SettingsReport.styles";
import ClientPicker from "./ClientPicker";
import { PerformanceReportTypeEnum } from "helpers/types";
import AssetPicker from "./AssetPicker";
import DateSelector, { BucketInceptionOption } from "../Statistic/DateSelector";
import { AccountBucketSummary } from "components/buckets/AccountBucketSummary";
import { HeldPeriod } from "features/trading/heldPeriods";
import { ClientBucket3Selector } from "components/buckets/ClientBucket3Selector";
import {
  BucketInceptionRangeReason,
  BucketNumberEnum,
  useGetBucketInceptionRangeQuery,
} from "features/trading/buckets";
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import dayjs from "dayjs";
import { useReportSettings } from "./ReportSettingsContext";
import { useSettingsReportValue } from "hooks/useReportStorage";
import ReportSettingsModal from "./ReportSettingsModal";
import CumulativeIncomeGlobalPeriodSetting from "./CumulativeIncomeGlobalPeriodSetting";
import ReportAccountHistory from "./ReportAccountHistory";
import {
  useGetAccountHistoryQuery,
  useGetStrategyByIdQuery,
} from "features/trading/accounts";
import { AdjustmentTypeEnum, optionTypeFromCallOrPut } from "helpers/types";
import { DateSelectionMode } from "../../../constants";
import { useNavigate } from "react-router-dom";

interface Props {
  type: PerformanceReportTypeEnum;
  header?: string;
  subtitle?: string;
  // Locks the client selector (ClientPicker's `disabled`).
  isClientLocked?: boolean;
  // Rendered in the filters box's top-right corner -- the Strategy Report's
  // Settings gear (StrategyReportSettings).
  settingsAction?: ReactNode;
}

type DateRange = [string | null, string | null];
const PERIOD_RANGES_KEY_PREFIX = "period_ranges";
const GLOBAL_CUMULATIVE_PERIOD_RANGES_KEY_PREFIX =
  "global_cumulative_period_ranges";
// The Period option picked last (Strategy Report only), so a new visit opens
// on it rather than on Year. The dates themselves are the period ranges above.
const PERIOD_MODE_KEY_PREFIX = "period_mode";

const getPeriodRangesKey = (type: PerformanceReportTypeEnum) =>
  `${PERIOD_RANGES_KEY_PREFIX}_${type}`;

const getGlobalCumulativePeriodRangesKey = (type: PerformanceReportTypeEnum) =>
  `${GLOBAL_CUMULATIVE_PERIOD_RANGES_KEY_PREFIX}_${type}`;

const parseDateRanges = (value: number | string): DateRange[] => {
  if (typeof value !== "string") return [];

  try {
    const parsed = JSON.parse(value);
    if (!Array.isArray(parsed)) return [];

    return parsed.filter(
      (range): range is DateRange =>
        Array.isArray(range) &&
        range.length === 2 &&
        (range[0] === null || typeof range[0] === "string") &&
        (range[1] === null || typeof range[1] === "string"),
    );
  } catch {
    return [];
  }
};

const getPeriodFromDateRanges = (ranges: DateRange[]): [string, string] => {
  const firstRange = ranges[0];
  if (!firstRange?.[0] || !firstRange?.[1]) return ["", ""];
  return [
    dayjs(firstRange[0]).format("YYYY-MM-DD"),
    dayjs(firstRange[1]).format("YYYY-MM-DD"),
  ];
};

const getPeriodModeKey = (type: PerformanceReportTypeEnum) =>
  `${PERIOD_MODE_KEY_PREFIX}_${type}`;

// The options the Strategy Report shows; anything else stored starts on Year.
const REMEMBERED_PERIOD_MODES: (number | string)[] = [
  DateSelectionMode.YEAR,
  DateSelectionMode.MONTH,
  DateSelectionMode.RANGE,
  DateSelectionMode.BUCKET_INCEPTION,
];

const parsePeriodMode = (
  value: number | string,
): DateSelectionMode | undefined =>
  REMEMBERED_PERIOD_MODES.includes(value)
    ? (value as DateSelectionMode)
    : undefined;

const toOptionalPeriod = (
  period: [string, string],
): [string, string] | undefined => {
  const [startDate, endDate] = period;
  if (!startDate || !endDate) return undefined;
  return [startDate, endDate];
};

/**
 * Every reporting period the settings hold — the period chips AND the
 * global-cumulative ones — as the bucket asset picker's restriction: a
 * security qualifies when the account held it during ANY of them, so one
 * bucket serves every period the report covers.
 *
 * A range with a bound still unchosen is dropped rather than half-applied,
 * and an identical range appearing in both lists is kept once.
 */
const toHeldPeriods = (rangeLists: DateRange[][]): HeldPeriod[] => {
  const seen = new Set<string>();
  const periods: HeldPeriod[] = [];
  rangeLists.flat().forEach(([startDate, endDate]) => {
    if (!startDate || !endDate) return;
    const period: HeldPeriod = [
      dayjs(startDate).format("YYYY-MM-DD"),
      dayjs(endDate).format("YYYY-MM-DD"),
    ];
    const key = period.join(":");
    if (seen.has(key)) return;
    seen.add(key);
    periods.push(period);
  });
  return periods;
};

const BUCKET_INCEPTION_DISABLED_REASONS: Record<
  BucketInceptionRangeReason,
  string
> = {
  no_buckets: "Select the bucket",
  no_inception_date: "None of the selected buckets has an inception date",
};

const sortAndUniqDateRanges = (ranges: DateRange[]): DateRange[] => {
  const deduped = ranges.filter(
    ([startDate, endDate], index, arr) =>
      arr.findIndex(([s, e]) => `${s}-${e}` === `${startDate}-${endDate}`) ===
      index,
  );
  return deduped.sort((a, b) => dayjs(a[0]).diff(dayjs(b[0])));
};

const SettingsReport = ({
  type,
  header = "Optic Equity Access Performance",
  subtitle = "OPTIC ASSET MANAGEMENT",
  isClientLocked = false,
  settingsAction,
}: Props) => {
  const settingsPeriodRangesKey = getPeriodRangesKey(type);
  const globalCumulativePeriodRangesKey =
    getGlobalCumulativePeriodRangesKey(type);
  const [storedDateRanges, setStoredDateRanges] = useSettingsReportValue(
    settingsPeriodRangesKey,
    "[]",
  );
  const [
    storedGlobalCumulativeDateRanges,
    setStoredGlobalCumulativeDateRanges,
  ] = useSettingsReportValue(globalCumulativePeriodRangesKey, "[]");
  const [dateRanges, setDateRanges] = useState<DateRange[]>(() =>
    parseDateRanges(storedDateRanges),
  );
  const [globalCumulativeDateRanges, setGlobalCumulativeDateRanges] = useState<
    DateRange[]
  >(() => parseDateRanges(storedGlobalCumulativeDateRanges));
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const isStrategy = type === PerformanceReportTypeEnum.STRATEGY;
  // Only the initial value is used: the selector takes it as its starting option.
  const [storedPeriodMode, setStoredPeriodMode] = useSettingsReportValue(
    isStrategy ? getPeriodModeKey(type) : "",
    "",
  );
  const [defaultPeriodMode] = useState(() => parsePeriodMode(storedPeriodMode));
  const handlePeriodModeChange = useCallback(
    (mode: DateSelectionMode) => setStoredPeriodMode(mode),
    [setStoredPeriodMode],
  );
  const { updateSettings, settingsInfo } = useReportSettings();
  const navigate = useNavigate();
  const period = useMemo(
    () => getPeriodFromDateRanges(dateRanges),
    [dateRanges],
  );
  const globalCumulativePeriod = useMemo(
    () => getPeriodFromDateRanges(globalCumulativeDateRanges),
    [globalCumulativeDateRanges],
  );
  const heldPeriods = useMemo(
    () => toHeldPeriods([dateRanges, globalCumulativeDateRanges]),
    [dateRanges, globalCumulativeDateRanges],
  );
  const inceptionAccountId = settingsInfo.account_id ?? null;
  const inceptionStrategyId = settingsInfo.strategy_id ?? null;
  const { data: inceptionStrategy } = useGetStrategyByIdQuery(
    inceptionStrategyId as number,
    { skip: !inceptionStrategyId },
  );
  const inceptionAssetId = inceptionStrategy?.asset ?? null;
  const inceptionOptionType = inceptionStrategy
    ? optionTypeFromCallOrPut(inceptionStrategy.call_or_put)
    : undefined;
  const { data: inceptionHistory } = useGetAccountHistoryQuery(
    {
      account_id: inceptionAccountId!,
      asset_id: inceptionAssetId!,
      // INCEPTION lives on the PORTFOLIO chain (no option_type), so we omit it.
      ordering: "adjustment_date",
      adjustment_type: AdjustmentTypeEnum.INCEPTION,
    },
    { skip: !inceptionAccountId || !inceptionAssetId },
  );
  const inceptionDate = inceptionHistory?.[0]?.adjustment_date ?? null;
  const { data: removedHistory } = useGetAccountHistoryQuery(
    {
      account_id: inceptionAccountId!,
      asset_id: inceptionAssetId!,
      option_type: inceptionOptionType,
      ordering: "-adjustment_date",
      adjustment_type: AdjustmentTypeEnum.REMOVED,
    },
    { skip: !inceptionAccountId || !inceptionAssetId },
  );
  const removedDate = removedHistory?.[0]?.adjustment_date ?? null;
  // Fetched as soon as the page knows the account, so the option's label
  // carries its dates before anyone opens it.
  const bucketInceptionAccountId = settingsInfo.account_id ?? null;
  const bucketInception3Id = settingsInfo.bucket_3_id ?? undefined;
  const {
    currentData: bucketInceptionRange,
    isError: bucketInceptionRangeFailed,
  } = useGetBucketInceptionRangeQuery(
    {
      account: bucketInceptionAccountId!,
      bucket_3: bucketInception3Id,
      // The browser's date, not the server's UTC one — see the endpoint.
      today: dayjs().format("YYYY-MM-DD"),
    },
    { skip: !isStrategy || !bucketInceptionAccountId },
  );
  // An account or Bucket 3 REPLACED after load (not the null -> id of the
  // page loading them) ends a remembered Bucket Inception's hold on its
  // stored period.
  const [inceptionSourceSwitched, setInceptionSourceSwitched] = useState(false);
  const inceptionSourceRef = useRef({
    account: bucketInceptionAccountId,
    bucket3: bucketInception3Id,
  });
  useEffect(() => {
    const prev = inceptionSourceRef.current;
    const replaced = (before: unknown, now: unknown) =>
      before != null && before !== now;
    if (
      replaced(prev.account, bucketInceptionAccountId) ||
      replaced(prev.bucket3, bucketInception3Id)
    ) {
      setInceptionSourceSwitched(true);
    }
    inceptionSourceRef.current = {
      account: bucketInceptionAccountId,
      bucket3: bucketInception3Id,
    };
  }, [bucketInceptionAccountId, bucketInception3Id]);
  const bucketInception = useMemo<BucketInceptionOption | undefined>(() => {
    if (!isStrategy) return undefined;
    if (!bucketInceptionAccountId) {
      return { range: null, disabledReason: "Select the account" };
    }
    if (bucketInceptionRangeFailed) {
      return {
        range: null,
        disabledReason: "Couldn't load the bucket inception range",
      };
    }
    if (!bucketInceptionRange) return { range: null, disabledReason: null };
    const { start_date, end_date, reason } = bucketInceptionRange;
    if (start_date && end_date) {
      return { range: [start_date, end_date], disabledReason: null };
    }
    return {
      range: null,
      disabledReason: reason ? BUCKET_INCEPTION_DISABLED_REASONS[reason] : null,
    };
  }, [
    isStrategy,
    bucketInceptionAccountId,
    bucketInceptionRange,
    bucketInceptionRangeFailed,
  ]);

  useEffect(() => {
    setDateRanges(parseDateRanges(storedDateRanges));
  }, [storedDateRanges]);

  useEffect(() => {
    setGlobalCumulativeDateRanges(
      parseDateRanges(storedGlobalCumulativeDateRanges),
    );
  }, [storedGlobalCumulativeDateRanges]);

  useEffect(() => {
    setStoredDateRanges(JSON.stringify(dateRanges));
  }, [dateRanges, setStoredDateRanges]);

  useEffect(() => {
    setStoredGlobalCumulativeDateRanges(
      JSON.stringify(globalCumulativeDateRanges),
    );
  }, [globalCumulativeDateRanges, setStoredGlobalCumulativeDateRanges]);

  useEffect(() => {
    updateSettings({ period });
  }, [period, updateSettings]);

  useEffect(() => {
    updateSettings({
      global_cumulative_period: toOptionalPeriod(globalCumulativePeriod),
    });
  }, [globalCumulativePeriod, updateSettings]);

  // Bucket Inception is exclusive: picking it leaves only its range, and
  // picking any other period drops that range again — the one it put in
  // (remembered, since the option's range may have changed or vanished
  // since) and, for chips restored from storage, the one it would put in now.
  // A remembered Bucket Inception pick restores its stored chip, not today's
  // range, so that chip is the one it put in.
  const insertedBucketInceptionRef = useRef<DateRange | null>(
    defaultPeriodMode === DateSelectionMode.BUCKET_INCEPTION &&
      dateRanges.length === 1
      ? dateRanges[0]
      : null,
  );
  const bucketInceptionStart = bucketInception?.range?.[0];
  const bucketInceptionEnd = bucketInception?.range?.[1];
  const handleDateChange = useCallback(
    (range: DateRange) => {
      const [startDate, endDate] = range;
      if (startDate && endDate) {
        const inserted = insertedBucketInceptionRef.current;
        insertedBucketInceptionRef.current = null;
        setDateRanges((prev) =>
          sortAndUniqDateRanges([
            ...prev.filter(
              ([s, e]) =>
                !(s === bucketInceptionStart && e === bucketInceptionEnd) &&
                !(inserted && s === inserted[0] && e === inserted[1]),
            ),
            range,
          ]),
        );
      }
    },
    [bucketInceptionStart, bucketInceptionEnd],
  );

  const handleBucketInceptionSelect = useCallback((range: [string, string]) => {
    insertedBucketInceptionRef.current = range;
    setDateRanges([range]);
  }, []);

  const handleRemoveDateRange = useCallback((index: number) => {
    setDateRanges((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleBucket3Change = useCallback(
    (bucketId: number | null) => updateSettings({ bucket_3_id: bucketId }),
    [updateSettings],
  );

  const handleGoToClient = useCallback(() => {
    if (!settingsInfo.client_id) return;
    const params = new URLSearchParams();
    params.set("client_id", String(settingsInfo.client_id));
    if (settingsInfo.account_id)
      params.set("account_id", String(settingsInfo.account_id));
    if (settingsInfo.strategy_id)
      params.set("strategy_id", String(settingsInfo.strategy_id));
    navigate(`/clients?${params.toString()}`);
  }, [
    settingsInfo.client_id,
    settingsInfo.account_id,
    settingsInfo.strategy_id,
    navigate,
  ]);

  const handleGlobalCumulativeDateChange = useCallback((range: DateRange) => {
    const [startDate, endDate] = range;
    if (startDate && endDate) {
      setGlobalCumulativeDateRanges((prev) =>
        sortAndUniqDateRanges([...prev, range]),
      );
    }
  }, []);

  const handleRemoveGlobalCumulativeDateRange = useCallback((index: number) => {
    setGlobalCumulativeDateRanges((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const formatDateRange = (range: DateRange) => {
    const [startDate, endDate] = range;
    if (startDate && endDate) {
      return `${dayjs(startDate).format("MM/DD/YYYY")} - ${dayjs(endDate).format("MM/DD/YYYY")}`;
    }
    return "";
  };

  return (
    <Box sx={settingsReportStyles.container}>
      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={12} md={7}>
          <Typography
            variant="h3"
            component="h1"
            sx={settingsReportStyles.mainTitle}
          >
            {header}
          </Typography>
          <Typography
            variant="body1"
            component="h2"
            sx={settingsReportStyles.subtitle}
          >
            {subtitle}
          </Typography>
        </Grid>

        <Grid item xs={12} md={5}>
          <Box sx={settingsReportStyles.summaryBox}>
            {settingsAction && (
              <Box sx={settingsReportStyles.settingsAction}>
                {settingsAction}
              </Box>
            )}
            <ClientPicker type={type} disabled={isClientLocked} />

            <Box sx={settingsReportStyles.summaryItem}>
              <Typography component="span" sx={settingsReportStyles.label}>
                Period:
              </Typography>
              <DateSelector
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                }}
                onDateChange={handleDateChange}
                inceptionDate={inceptionDate}
                removedDate={removedDate}
                bucketInception={bucketInception}
                onBucketInceptionSelect={handleBucketInceptionSelect}
                defaultMode={isStrategy ? defaultPeriodMode : undefined}
                onModeChange={isStrategy ? handlePeriodModeChange : undefined}
                releaseRestoredPick={inceptionSourceSwitched}
                variant={isStrategy ? "dropdown" : "radios"}
                additionalComponents={
                  dateRanges.length > 0 ? (
                    <Box sx={settingsReportStyles.periodChipContainer}>
                      {dateRanges.map((range, index) => (
                        <Chip
                          key={`${range[0]}-${range[1]}`}
                          label={formatDateRange(range)}
                          onDelete={() => handleRemoveDateRange(index)}
                          size="small"
                          sx={settingsReportStyles.periodChip}
                        />
                      ))}
                    </Box>
                  ) : null
                }
              />
            </Box>
            {isStrategy ? (
              <>
                {[BucketNumberEnum.BUCKET_1, BucketNumberEnum.BUCKET_2].map(
                  (bucketNumber) => (
                    <Box
                      key={bucketNumber}
                      sx={settingsReportStyles.summaryItem}
                    >
                      <Typography
                        component="span"
                        sx={settingsReportStyles.label}
                      >
                        Bucket {bucketNumber}:
                      </Typography>
                      <AccountBucketSummary
                        accountId={settingsInfo.account_id}
                        accountLabel={settingsInfo.account}
                        number={bucketNumber}
                        heldPeriods={heldPeriods}
                      />
                    </Box>
                  ),
                )}
                <Box sx={settingsReportStyles.summaryItem}>
                  <Typography component="span" sx={settingsReportStyles.label}>
                    Bucket 3:
                  </Typography>
                  <ClientBucket3Selector
                    clientId={settingsInfo.client_id}
                    onBucket3Change={handleBucket3Change}
                  />
                </Box>
              </>
            ) : (
              <AssetPicker type={type} />
            )}
            <Box sx={settingsReportStyles.summaryItem}>
              {isStrategy ? (
                <Button
                  variant="outlined"
                  size="small"
                  disabled={!settingsInfo.client_id}
                  onClick={handleGoToClient}
                >
                  Go to Client
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setIsSettingsModalOpen(true)}
                >
                  Open Settings
                </Button>
              )}
            </Box>
          </Box>
        </Grid>
      </Grid>
      {!isStrategy && (
        <ReportSettingsModal
          open={isSettingsModalOpen}
          onClose={() => setIsSettingsModalOpen(false)}
        >
          {type === PerformanceReportTypeEnum.STOCK && (
            <CumulativeIncomeGlobalPeriodSetting
              dateRanges={globalCumulativeDateRanges}
              onDateChange={handleGlobalCumulativeDateChange}
              onRemoveDateRange={handleRemoveGlobalCumulativeDateRange}
            />
          )}
          <ReportAccountHistory
            accountId={settingsInfo.account_id ?? null}
            strategyId={settingsInfo.strategy_id ?? null}
          />
          <Box sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              size="small"
              disabled={!settingsInfo.client_id}
              onClick={handleGoToClient}
            >
              Go to Client
            </Button>
          </Box>
        </ReportSettingsModal>
      )}
    </Box>
  );
};

export default SettingsReport;
